import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import { supabase, signInWithApple, signInWithGoogle } from '../../lib/supabase'
import { trackSignIn } from '../../lib/analytics'
import { storePasswordCredential } from '../../lib/passwordCredentials'
import { isNativeIOS } from '../../mobile/capacitor'

type Props = { onSuccess?: () => void }

export default function SignInForm({ onSuccess }: Props) {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [sendingReset, setSendingReset] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true)
      setError(null)
      await signInWithGoogle()
      trackSignIn('google')
      setGoogleLoading(false)
      // User will be redirected to Google, then back to dashboard
      // No need to call onSuccess here as redirect happens
    } catch (err) {
      setGoogleLoading(false)
      const errorMsg = err instanceof Error ? err.message : 'Failed to sign in with Google'
      setError(errorMsg)
      toast.error(errorMsg)
    }
  }

  const handleAppleSignIn = async () => {
    try {
      setAppleLoading(true)
      setError(null)
      await signInWithApple()
      trackSignIn('apple')
      toast.success('Welcome back! You are now signed in.')
      onSuccess?.()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to sign in with Apple'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setAppleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const errorMsg = 'Enter a valid email'
      setError(errorMsg)
      toast.error(errorMsg)
      return
    }
    if (!password) {
      const errorMsg = 'Enter your password'
      setError(errorMsg)
      toast.error(errorMsg)
      return
    }
    setSubmitting(true)
    const res = await signIn({ email, password })
    setSubmitting(false)
    if (res.error) {
      setError(res.error)
      toast.error(res.error)
      return
    }
    await storePasswordCredential({ email, password })
    trackSignIn('email')
    toast.success('Welcome back! You are now signed in.')
    onSuccess?.()
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      toast.error('Please enter a valid email address')
      return
    }

    setSendingReset(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        // Check if it's an email service error
        const isEmailServiceError =
          error.message.includes('500') ||
          error.message.toLowerCase().includes('email') ||
          error.message.toLowerCase().includes('smtp')

        const errorMessage = isEmailServiceError
          ? 'Email service is temporarily unavailable. Please try again later or contact support.'
          : error.message

        toast.error(errorMessage)
      } else {
        toast.success('Password reset email sent! Check your inbox.')
        setShowForgotPassword(false)
        setResetEmail('')
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send reset email. Please try again later.'
      toast.error(errorMessage)
    } finally {
      setSendingReset(false)
    }
  }

  return (
    <div className="w-full space-y-4">
      {error && (
        <div
          role="alert"
          className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3"
        >
          {error}
        </div>
      )}

      {isNativeIOS() && (
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={appleLoading || googleLoading || submitting}
          aria-busy={appleLoading}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-black bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-900 disabled:opacity-60"
        >
          <span className="text-xl leading-none" aria-hidden="true">
            &#63743;
          </span>
          {appleLoading ? 'Connecting to Apple...' : 'Continue with Apple'}
        </button>
      )}

      {/* Google Sign In Button */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || appleLoading || submitting}
        className="w-full flex items-center justify-center gap-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {googleLoading ? 'Connecting to Google...' : 'Continue with Google'}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-300 dark:border-slate-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">
            or
          </span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form id="signin-form" onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        <div className="space-y-1">
          <label
            htmlFor="signin-username"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Email
          </label>
          <input
            id="signin-username"
            name="username"
            type="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
            placeholder="you@example.com"
          />
        </div>
        <div className="space-y-1">
          <label
            htmlFor="signin-current-password"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="signin-current-password"
              name="current-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 pr-11 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
              placeholder="********"
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-slate-500 transition-colors hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-400 dark:hover:text-slate-200 dark:focus-visible:ring-amber-500"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting || googleLoading || appleLoading}
          className="w-full rounded-md bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900 py-2 disabled:opacity-60 hover:bg-brand-700 dark:hover:bg-amber-400 transition-colors font-semibold"
        >
          {submitting ? 'Signing in...' : 'Sign in'}
        </button>

        {/* Forgot Password Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-brand-600 dark:text-amber-400 hover:text-brand-700 dark:hover:text-amber-300 font-medium"
          >
            Forgot your password?
          </button>
        </div>
      </form>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              Reset Your Password
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label
                  htmlFor="reset-username"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Email Address
                </label>
                <input
                  id="reset-username"
                  name="username"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 shadow-sm focus:border-brand-500 dark:focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 transition-colors"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetEmail('')
                  }}
                  className="flex-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  disabled={sendingReset}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingReset}
                  className="flex-1 rounded-md bg-brand-600 dark:bg-amber-500 px-4 py-2 text-sm font-medium text-white dark:text-slate-900 hover:bg-brand-700 dark:hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingReset ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
